import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Select } from "../../components/ui/input";
import { Modal } from "../../components/ui/modal";
import { AccountFormModal } from "../../components/crm/account-form-modal";
import { ActivateDialog } from "../../components/crm/activate-dialog";
import { ActivityFeed } from "../../components/crm/activity-feed";
import { ContactList } from "../../components/crm/contact-list";
import { EditableSection, type EditableField } from "../../components/crm/editable-section";
import { CrmStatusBadge } from "../../components/crm/status-badge";
import { MeetingList } from "../../components/crm/meeting-list";
import { OpportunityList } from "../../components/crm/opportunity-list";
import { RevenueList } from "../../components/crm/revenue-list";
import { TaskList } from "../../components/crm/task-list";
import { TranscriptImport } from "../../components/crm/transcript-import";
import {
  CRM_PARTNER_LABELS,
  CRM_SECTOR_LABELS,
  CRM_SOURCE_LABELS,
  CRM_STATUS_LABELS,
  CRM_STATUS_ORDER,
  formatCents,
  formatRelativeDay,
} from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmAccountDetail, CrmAccountStatus } from "@shared/types";

const TABS = [
  { key: "resume", label: "Résumé" },
  { key: "suivis", label: "Suivis" },
  { key: "rendez-vous", label: "Rendez-vous" },
  { key: "opportunites", label: "Opportunités" },
  { key: "revenus", label: "Revenus" },
  { key: "historique", label: "Historique" },
] as const;

type TabKey = (typeof TABS)[number]["key"];
type Failure = "not_found" | "forbidden" | "error" | null;

const FAILURE_MESSAGES: Record<Exclude<Failure, null>, string> = {
  not_found: "Dossier introuvable — il a peut-être été supprimé.",
  forbidden: "Le CRM est réservé aux administrateurs. Demande un accès admin pour voir ce dossier.",
  error: "Fiche indisponible — réessayez dans un instant.",
};

/** Chaque code d'erreur a son message : « introuvable » et « panne » ne se soignent pas pareil. */
function failureOf(err: unknown): Failure {
  if (!(err instanceof ApiError)) return "error";
  if (err.status === 404) return "not_found";
  if (err.status === 403) return "forbidden";
  return "error";
}

interface UserOption {
  id: string;
  name: string;
  role: "admin" | "user";
}

// Les blocs de l'onglet Résumé. Chacun s'ouvre seul : on remplit la fiche au fil
// de l'eau plutôt que d'affronter les vingt-deux champs du formulaire complet.
const CONTACT_FIELDS: EditableField[] = [
  { key: "contactName", label: "Nom", type: "text", placeholder: "ex : Marie Tremblay" },
  { key: "contactRole", label: "Rôle", type: "text", placeholder: "ex : Propriétaire" },
  { key: "contactEmail", label: "Courriel", type: "email" },
  { key: "contactPhone", label: "Téléphone", type: "tel", placeholder: "ex : 514 555-0134" },
];

const ADDRESS_FIELDS: EditableField[] = [
  { key: "addressLine", label: "Adresse", type: "text" },
  { key: "city", label: "Ville", type: "text" },
  { key: "province", label: "Province", type: "text", placeholder: "ex : QC" },
  { key: "postalCode", label: "Code postal", type: "text", placeholder: "ex : H2X 1Y4" },
  { key: "country", label: "Pays", type: "text", required: true },
];

const CONTRACT_FIELDS: EditableField[] = [
  { key: "setupFeeCents", label: "Frais d'installation", type: "money" },
  { key: "mrrCents", label: "Abonnement mensuel", type: "money", hint: "MRR contractuel." },
  {
    key: "commissionPct",
    label: "Commission (%)",
    type: "percent",
    hint: "Taux appliqué par défaut aux lignes de revenus.",
  },
  { key: "contractStartAt", label: "Début de contrat", type: "date" },
  { key: "contractEndAt", label: "Fin de contrat", type: "date" },
];

const NOTES_FIELDS: EditableField[] = [
  {
    key: "notes",
    label: "Notes",
    type: "textarea",
    placeholder: "Contexte, historique, points d'attention…",
  },
];

function toOptions<K extends string>(labels: Record<K, string>): EditableField["options"] {
  return (Object.entries(labels) as Array<[K, string]>).map(([value, label]) => ({ value, label }));
}

export function CrmAccount() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState<CrmAccountDetail | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [editing, setEditing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Un seul appel : /api/crm/accounts/:id renvoie le dossier et ses cinq
  // collections. Chaque mutation d'un enfant rappelle simplement cette fonction.
  const load = useCallback(() => {
    if (!accountId) return;
    api<CrmAccountDetail>(`/api/crm/accounts/${accountId}`)
      .then((d) => {
        setDetail(d);
        setFailure(null);
      })
      .catch((err: unknown) => {
        // Un dossier supprimé dans un autre onglet ne doit pas laisser la fiche
        // sur « Chargement… » : chaque échec a son écran.
        setDetail(null);
        setFailure(failureOf(err));
      });
  }, [accountId]);

  useEffect(load, [load]);

  // Les responsables ne sont pas dans le détail : on résout les identifiants
  // (assignations, auteurs de notes) avec la liste des utilisateurs.
  useEffect(() => {
    api<UserOption[]>("/api/users")
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  // Un dossier n'est attribuable qu'à un admin — même règle que le formulaire complet.
  const clientFields = useMemo<EditableField[]>(
    () => [
      {
        key: "legalName",
        label: "Raison sociale",
        type: "text",
        hint: "Nom légal si différent du nom commercial.",
      },
      { key: "sector", label: "Secteur", type: "select", options: toOptions(CRM_SECTOR_LABELS) },
      { key: "source", label: "Source", type: "select", options: toOptions(CRM_SOURCE_LABELS) },
      {
        key: "partner",
        label: "Partenaire",
        type: "select",
        options: toOptions(CRM_PARTNER_LABELS),
      },
      {
        key: "ownerUserId",
        label: "Responsable",
        type: "select",
        options: [
          { value: "", label: "Non attribué" },
          ...users.filter((u) => u.role === "admin").map((u) => ({ value: u.id, label: u.name })),
        ],
      },
      { key: "website", label: "Site web", type: "url", placeholder: "https://…" },
    ],
    [users],
  );

  const rawTab = searchParams.get("tab");
  const tab: TabKey = TABS.some((t) => t.key === rawTab) ? (rawTab as TabKey) : "resume";
  const selectTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const changeStatus = async (status: CrmAccountStatus) => {
    if (!accountId) return;
    try {
      await api(`/api/crm/accounts/${accountId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      push("success", `Statut : ${CRM_STATUS_LABELS[status]}`);
      load();
    } catch {
      push("danger", "Échec du changement de statut");
    }
  };

  const remove = async () => {
    if (!accountId || deleting) return;
    setDeleting(true);
    try {
      await api(`/api/crm/accounts/${accountId}`, { method: "DELETE" });
      push("success", "Dossier supprimé");
      navigate("/crm");
    } catch {
      push("danger", "Échec de la suppression");
      setDeleting(false);
    }
  };

  if (failure) {
    return (
      <div>
        <div className="crm-linkline">
          <Link to="/crm">← Retour au pipeline</Link>
        </div>
        <p className="crm-empty">{FAILURE_MESSAGES[failure]}</p>
      </div>
    );
  }

  if (!detail || !accountId) return <p className="crm-empty">Chargement…</p>;

  const a = detail.account;
  const stats = detail.stats;
  const tags = parseTags(a.tagsJson);

  return (
    <div className="crm-detail">
      <div className="crm-linkline">
        <Link to="/crm">← Retour au pipeline</Link>
      </div>

      <header className="crm-detail__head">
        <div className="crm-detail__titles">
          <h1 className="crm-detail__name">{a.name}</h1>
          <p className="crm-detail__sub">
            <CrmStatusBadge status={a.status} />
            <span>{CRM_SECTOR_LABELS[a.sector]}</span>
            <span>·</span>
            <span>{CRM_PARTNER_LABELS[a.partner]}</span>
            <span>·</span>
            <span>{detail.ownerName ?? "Sans responsable"}</span>
            {tags.map((t) => (
              <span key={t} className="crm-chip crm-chip--muted">
                {t}
              </span>
            ))}
          </p>
          {a.tenantId ? (
            <span className="crm-linkline">
              Établissement lié
              <Link to={`/tenants/${a.tenantId}/settings`}>{detail.tenantName ?? a.tenantId}</Link>
            </span>
          ) : null}
        </div>
        <div className="crm-detail__actions">
          <Select
            aria-label="Statut du dossier"
            value={a.status}
            // .select fait 100 % par défaut : dans la barre d'actions il rejetterait les boutons à la ligne.
            style={{ width: "auto" }}
            onChange={(e) => void changeStatus(e.target.value as CrmAccountStatus)}
          >
            {CRM_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {CRM_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
          {!a.tenantId ? (
            <Button variant="ghost" size="sm" onClick={() => setActivating(true)}>
              Activer
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => setImporting(true)}>
            Coller un compte rendu
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Modifier
          </Button>
          <Button variant="ghost-danger" size="sm" onClick={() => setConfirming(true)}>
            Supprimer
          </Button>
        </div>
      </header>

      <div className="crm-stats">
        <div className="crm-stat crm-stat--accent">
          <span className="crm-stat__label">Encaissé</span>
          <span className="crm-stat__value">{formatCents(stats.collectedCents, a.currency)}</span>
          <span className="crm-stat__hint">Lignes marquées encaissées</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Facturé</span>
          <span className="crm-stat__value">{formatCents(stats.invoicedCents, a.currency)}</span>
          <span className="crm-stat__hint">En attente d'encaissement</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Prévisionnel</span>
          <span className="crm-stat__value">{formatCents(stats.forecastCents, a.currency)}</span>
          <span className="crm-stat__hint">Lignes prévues</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Commission due</span>
          <span className="crm-stat__value">{formatCents(stats.commissionCents, a.currency)}</span>
          <span className="crm-stat__hint">Taux du dossier : {a.commissionPct} %</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Suivis ouverts</span>
          <span className="crm-stat__value">{stats.openTaskCount}</span>
          <span className="crm-stat__hint">
            {stats.overdueTaskCount > 0 ? `${stats.overdueTaskCount} en retard` : "Rien en retard"}
          </span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Prochain RDV</span>
          <span className="crm-stat__value">{formatRelativeDay(stats.nextMeetingAt)}</span>
          <span className="crm-stat__hint">
            {stats.nextMeetingAt
              ? formatDateTime(stats.nextMeetingAt, { dateStyle: "medium", timeStyle: "short" })
              : "Aucun rendez-vous planifié"}
          </span>
        </div>
      </div>

      <div className="crm-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`crm-tabs__btn${tab === t.key ? " is-active" : ""}`}
            onClick={() => selectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="crm-detail__body">
        <div className="crm-detail__main">
          {tab === "resume" ? (
            <>
              <EditableSection
                title="Fiche client"
                fields={clientFields}
                account={a}
                onSaved={load}
              />
              <EditableSection
                title="Contact principal"
                fields={CONTACT_FIELDS}
                account={a}
                onSaved={load}
              />
              <EditableSection title="Adresse" fields={ADDRESS_FIELDS} account={a} onSaved={load} />
              <EditableSection
                title="Contrat"
                fields={CONTRACT_FIELDS}
                account={a}
                onSaved={load}
              />
              <EditableSection title="Notes" fields={NOTES_FIELDS} account={a} onSaved={load} />

              <section className="crm-section">
                <div className="crm-section__head">
                  <h2 className="crm-section__title">Jalons</h2>
                </div>
                <dl className="crm-def">
                  <DefRow label="Activé le">
                    {formatDateTime(a.activatedAt, { dateStyle: "long" })}
                  </DefRow>
                  <DefRow label="Mise en service">
                    {formatDateTime(a.serviceStartedAt, { dateStyle: "long" })}
                  </DefRow>
                  {a.lostAt ? (
                    <DefRow label="Perdu le">
                      {formatDateTime(a.lostAt, { dateStyle: "long" })}
                      {a.lostReason ? ` — ${a.lostReason}` : ""}
                    </DefRow>
                  ) : null}
                  <DefRow label="Créé le">
                    {formatDateTime(a.createdAt, { dateStyle: "long" })}
                  </DefRow>
                  <DefRow label="Identifiant" mono>
                    {a.id}
                  </DefRow>
                </dl>
              </section>

              <ContactList accountId={accountId} items={detail.contacts} onChanged={load} />
            </>
          ) : null}

          {tab === "suivis" ? (
            <TaskList accountId={accountId} items={detail.tasks} users={users} onChanged={load} />
          ) : null}

          {tab === "rendez-vous" ? (
            <MeetingList
              accountId={accountId}
              items={detail.meetings}
              users={users}
              onChanged={load}
            />
          ) : null}

          {tab === "opportunites" ? (
            <OpportunityList
              accountId={accountId}
              items={detail.opportunities}
              currency={a.currency}
              users={users}
              onChanged={load}
            />
          ) : null}

          {tab === "revenus" ? (
            <RevenueList
              accountId={accountId}
              items={detail.revenues}
              currency={a.currency}
              commissionPct={a.commissionPct}
              onChanged={load}
            />
          ) : null}

          {tab === "historique" ? (
            <ActivityFeed
              accountId={accountId}
              items={detail.activities}
              users={users}
              onChanged={load}
            />
          ) : null}
        </div>

        <aside className="crm-detail__side">
          {tab === "historique" ? (
            <section className="crm-section">
              <div className="crm-section__head">
                <h2 className="crm-section__title">Repères</h2>
              </div>
              <dl className="crm-def">
                <DefRow label="Responsable">{detail.ownerName ?? "—"}</DefRow>
                <DefRow label="Établissement">{detail.tenantName ?? "Non lié"}</DefRow>
                <DefRow label="Créé le">
                  {formatDateTime(a.createdAt, { dateStyle: "medium" })}
                </DefRow>
                <DefRow label="Mis à jour">{formatRelativeDay(a.updatedAt)}</DefRow>
              </dl>
            </section>
          ) : (
            <ActivityFeed
              accountId={accountId}
              items={detail.activities}
              users={users}
              onChanged={load}
              title="Activité récente"
              limit={5}
              showComposer={false}
            />
          )}
        </aside>
      </div>

      <AccountFormModal
        open={editing}
        account={a}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          load();
        }}
      />

      <TranscriptImport
        accountId={accountId}
        open={importing}
        onClose={() => setImporting(false)}
        onApplied={() => {
          setImporting(false);
          load();
        }}
      />

      <ActivateDialog
        open={activating}
        account={a}
        onClose={() => setActivating(false)}
        onActivated={() => {
          setActivating(false);
          load();
        }}
      />

      <Modal
        open={confirming}
        title="Supprimer le dossier"
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={() => void remove()} disabled={deleting}>
              {deleting ? "Suppression…" : "Supprimer définitivement"}
            </Button>
          </>
        }
      >
        <p>
          « {a.name} » sera supprimé avec ses contacts, suivis, rendez-vous, opportunités, lignes de
          revenus et son historique. L'établissement lié, lui, n'est pas touché.
        </p>
      </Modal>
    </div>
  );
}

function DefRow({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <div className="crm-def__row">
      <dt className="crm-def__label">{label}</dt>
      <dd className={`crm-def__value${mono ? " crm-def__value--mono" : ""}`}>{children}</dd>
    </div>
  );
}

function parseTags(json: string | null): string[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === "string") : [];
  } catch {
    return [];
  }
}
